/**
 * DevModeAuthGate - Authentication gate for dev mode on staging
 * 
 * SECURITY:
 * - Only shown on staging.lykbl.com and staging.lykbl.ai
 * - Requires username/password to access dev mode features
 * - Session expires after 4 hours
 * - Credentials stored in sessionStorage (cleared on browser close)
 * - NOT shown on production or preview (dev mode is disabled there via domain blocklist)
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, AlertTriangle, Eye, EyeOff } from 'lucide-react';
import { 
  Button, 
  Input, 
  Label, 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle, 
  CardDescription 
} from '@lykbl/ui';
import { isStaging } from './DevModeContext';

// Dev mode credentials - only for staging environment
const DEV_MODE_CREDENTIALS = {
  username: 'lykbl_tester',
  password: 'LRVVA!R_2ZABYA'
};

// Session storage key for dev mode auth
const DEV_MODE_AUTH_KEY = 'lykbl:devModeAuth';
const DEV_MODE_AUTH_TIMESTAMP_KEY = 'lykbl:devModeAuthTimestamp';

// Auth session timeout (4 hours)
const AUTH_TIMEOUT_MS = 4 * 60 * 60 * 1000;

export interface DevModeAuthGateProps {
  children: React.ReactNode;
  onAuthSuccess?: () => void;
}

export const DevModeAuthGate: React.FC<DevModeAuthGateProps> = ({ children, onAuthSuccess }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // Check existing auth on mount
  useEffect(() => {
    // If not on staging, don't show auth gate
    if (!isStaging()) {
      setIsAuthenticated(true);
      setIsLoading(false);
      return;
    }

    // Check for existing valid session
    const authToken = sessionStorage.getItem(DEV_MODE_AUTH_KEY);
    const authTimestamp = sessionStorage.getItem(DEV_MODE_AUTH_TIMESTAMP_KEY);

    if (authToken === 'authenticated' && authTimestamp) {
      const timestamp = parseInt(authTimestamp, 10);
      const now = Date.now();

      // Check if session is still valid
      if (now - timestamp < AUTH_TIMEOUT_MS) {
        setIsAuthenticated(true);
        console.log('🔓 Dev mode auth: Session restored');
      } else {
        // Session expired
        sessionStorage.removeItem(DEV_MODE_AUTH_KEY);
        sessionStorage.removeItem(DEV_MODE_AUTH_TIMESTAMP_KEY);
        console.log('⏰ Dev mode auth: Session expired');
      }
    }

    setIsLoading(false);
  }, []);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate credentials
    if (
      username === DEV_MODE_CREDENTIALS.username &&
      password === DEV_MODE_CREDENTIALS.password
    ) {
      // Store auth in sessionStorage
      sessionStorage.setItem(DEV_MODE_AUTH_KEY, 'authenticated');
      sessionStorage.setItem(DEV_MODE_AUTH_TIMESTAMP_KEY, Date.now().toString());
      
      setIsAuthenticated(true);
      console.log('✅ Dev mode auth: Authenticated successfully');
      
      if (onAuthSuccess) {
        onAuthSuccess();
      }
    } else {
      setError('Invalid username or password');
      console.warn('❌ Dev mode auth: Authentication failed');
      
      // Clear fields on error
      setPassword('');
    }
  };

  // If loading, show nothing
  if (isLoading) {
    return null;
  }

  // If authenticated or not on staging, render children
  if (isAuthenticated) {
    return <>{children}</>;
  }

  // Show login form (only on staging)
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: 'spring', duration: 0.5 }}
        >
          <Card className="w-full max-w-md mx-4 shadow-2xl border-orange-500/20">
            <CardHeader className="space-y-1 text-center">
              <div className="mx-auto w-12 h-12 bg-gradient-to-br from-orange-500 to-amber-500 rounded-full flex items-center justify-center mb-4">
                <Lock className="h-6 w-6 text-white" />
              </div>
              <CardTitle className="text-2xl font-bold">Dev Mode Authentication</CardTitle>
              <CardDescription>
                Enter credentials to access development features on staging
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mb-4 p-3 border border-amber-500/50 bg-amber-500/10 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-amber-600 dark:text-amber-400">
                    <strong>Staging Environment</strong>
                    <br />
                    Dev mode requires authentication. This feature is disabled on production and preview.
                  </div>
                </div>
              </div>

              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="username">Username</Label>
                  <Input
                    id="username"
                    type="text"
                    placeholder="Enter username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    autoComplete="username"
                    required
                    className="border-gray-300 dark:border-gray-700"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="current-password"
                      required
                      className="border-gray-300 dark:border-gray-700 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>

                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-3 rounded-md border border-red-200 dark:border-red-800"
                  >
                    {error}
                  </motion.div>
                )}

                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600"
                >
                  <Lock className="mr-2 h-4 w-4" />
                  Authenticate
                </Button>
              </form>

              <div className="mt-6 pt-4 border-t text-center text-xs text-gray-500 dark:text-gray-400">
                <p>Session expires after 4 hours of inactivity</p>
                <p className="mt-1">Credentials are only valid on staging environments</p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

/**
 * Hook to check if dev mode is authenticated
 */
export const useDevModeAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const checkAuth = () => {
      const authToken = sessionStorage.getItem(DEV_MODE_AUTH_KEY);
      const authTimestamp = sessionStorage.getItem(DEV_MODE_AUTH_TIMESTAMP_KEY);

      if (authToken === 'authenticated' && authTimestamp) {
        const timestamp = parseInt(authTimestamp, 10);
        const now = Date.now();

        if (now - timestamp < AUTH_TIMEOUT_MS) {
          setIsAuthenticated(true);
        } else {
          sessionStorage.removeItem(DEV_MODE_AUTH_KEY);
          sessionStorage.removeItem(DEV_MODE_AUTH_TIMESTAMP_KEY);
          setIsAuthenticated(false);
        }
      }
    };

    checkAuth();
  }, []);

  const logout = () => {
    sessionStorage.removeItem(DEV_MODE_AUTH_KEY);
    sessionStorage.removeItem(DEV_MODE_AUTH_TIMESTAMP_KEY);
    setIsAuthenticated(false);
  };

  return { isAuthenticated, logout };
};

export default DevModeAuthGate;

